import { useEffect, useRef, useState } from 'react';
import type { Annotation } from '../types/annotations';
import { makeAnnotationId } from '../types/annotations';
import { useLanguage } from '../lib/language';

export interface AnnotationLayerProps {
  armed: boolean;
  annotations: Annotation[];
  onAdd?: (annotation: Annotation) => void;
  onUpdate?: (annotation: Annotation) => void;
  onRemove?: (id: string) => void;
  autoOpenLastId?: string | null;
  onAutoOpenConsumed?: () => void;
  disabled?: boolean;
}

/**
 * v7 — Annotation hotspots on top of the eye image. Click while `armed`
 * to drop a hotspot; click an existing hotspot to edit/read its note.
 */
export function AnnotationLayer(props: AnnotationLayerProps) {
  const { armed, annotations, onAdd, onUpdate, onRemove, autoOpenLastId, onAutoOpenConsumed, disabled } = props;
  const { lang } = useLanguage();
  const layerRef = useRef<HTMLDivElement | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);

  useEffect(() => {
    if (autoOpenLastId) {
      setOpenId(autoOpenLastId);
      onAutoOpenConsumed?.();
    }
  }, [autoOpenLastId, onAutoOpenConsumed]);

  const handleLayerClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!armed || disabled) return;
    if ((e.target as HTMLElement).closest('[data-annotation-marker]')) return;
    const rect = layerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    if (x < 0 || x > 1 || y < 0 || y > 1) return;
    onAdd?.({
      id: makeAnnotationId(),
      x, y, radius: 0.03, text: '',
      created_at: new Date().toISOString(),
    });
  };

  const placeholder = lang === 'plain' ? 'e.g. warmer, more gold' : 'annotation text';
  const hint = lang === 'plain' ? 'Click on the eye to add a note' : 'Annotation layer armed';

  return (
    <div
      ref={layerRef}
      data-testid="annotation-layer"
      data-armed={armed}
      onClick={handleLayerClick}
      className={`absolute inset-0 ${armed && !disabled ? 'cursor-crosshair' : 'pointer-events-none'}`}
    >
      {armed ? (
        <span
          aria-hidden="true"
          className="pointer-events-none absolute left-1/2 top-2 -translate-x-1/2 rounded-md border border-accent bg-accent/20 px-3 py-1 text-xs font-semibold text-accent backdrop-blur"
        >
          {hint}
        </span>
      ) : null}
      {annotations.map((a) => {
        const isOpen = openId === a.id;
        const radiusPct = (a.radius ?? 0.03) * 100;
        return (
          <div
            key={a.id}
            data-annotation-marker
            data-testid={`annotation-marker-${a.id}`}
            className="absolute"
            style={{ left: `${a.x * 100}%`, top: `${a.y * 100}%`, transform: 'translate(-50%, -50%)' }}
          >
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); if (disabled) return; setOpenId(isOpen ? null : a.id); }}
              aria-label={a.text ? `Annotation: ${a.text}` : 'Empty annotation — click to type a note'}
              className={`block cursor-pointer rounded-full border-2 border-accent bg-accent/30 shadow-[0_0_0_1px_rgba(0,0,0,0.4)] focus:outline-none focus-visible:ring-2 focus-visible:ring-accent motion-safe:transition-shadow ${armed ? 'h-6 w-6 hover:bg-accent/45' : 'h-5 w-5 bg-accent/20 hover:bg-accent/35'}`}
              style={{ padding: 4, width: `${Math.max(radiusPct * 0.6, 4)}%`, aspectRatio: '1 / 1', maxWidth: 44, maxHeight: 44 }}
            />
            {isOpen ? (
              <div
                data-annotation-marker
                className="absolute left-1/2 top-full z-20 mt-2 w-56 -translate-x-1/2 rounded-md border border-neutral-300 bg-white p-2 shadow-lg dark:border-neutral-700 dark:bg-neutral-900"
                onClick={(e) => e.stopPropagation()}
              >
                <textarea
                  data-testid={`annotation-input-${a.id}`}
                  value={a.text}
                  autoFocus
                  rows={2}
                  placeholder={placeholder}
                  onChange={(e) => onUpdate?.({ ...a, text: e.target.value })}
                  className="w-full rounded border border-neutral-300 bg-white p-1 text-xs text-neutral-900 focus:border-accent focus:outline-none focus-visible:ring-1 focus-visible:ring-accent dark:border-neutral-700 dark:bg-neutral-950 dark:text-neutral-100"
                />
                <div className="mt-1 flex items-center justify-between gap-1">
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onRemove?.(a.id); setOpenId(null); }}
                    className="rounded px-1.5 py-0.5 text-[10px] font-medium text-rose-600 hover:bg-rose-50 focus:outline-none focus-visible:ring-1 focus-visible:ring-rose-500 dark:text-rose-300 dark:hover:bg-rose-900/40"
                  >
                    Remove
                  </button>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setOpenId(null); }}
                    className="rounded bg-accent px-2 py-0.5 text-[10px] font-semibold text-neutral-900 hover:bg-accent-hover focus:outline-none focus-visible:ring-1 focus-visible:ring-accent"
                  >
                    Done
                  </button>
                </div>
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
